import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Paper,
  LinearProgress,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  AccountCircle as AccountCircleIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import leaksService from '../services/leaksService';
import authService from '../services/authService';
import NotificationsBell from '../components/NotificationsBell';
import MasterEdLogo from '../assets/masteredlogo-removebg-preview.png';
import '../styles/Dashboard.css';

const Leaks = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('all');

  const studentId = localStorage.getItem('student_id') || 'Unknown ID';
  const studentName = localStorage.getItem('student_name') || 'Unknown Student';
  const firstName = studentName.split(' ')[0] || 'Student';

  // Time-based greeting
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [testsResponse, statsResponse] = await Promise.all([
        leaksService.getAvailableTests(),
        leaksService.getMyStats()
      ]);

      setTests(testsResponse.data || []);
      setStats(statsResponse.data || {});
      setError(null);
    } catch (err) {
      console.error('Error loading leaks data:', err);
      setError('Failed to load tests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async (testId) => {
    try {
      const response = await leaksService.startTest(testId);
      const sessionId = response.data.sessionId;
      
      // Navigate to test taking page with testId as query param
      navigate(`/leaks/test/${sessionId}?leaksTestId=${testId}`);
    } catch (err) {
      console.error('Error starting test:', err);
      const errorMsg = err.response?.data?.error || 'Failed to start test. Please try again.';
      alert(errorMsg);
    }
  };

  const handleViewResults = (test) => {
    // Navigate to results page using session ID (not test ID)
    if (test.sessionId) {
      navigate(`/leaks/results/${test.sessionId}`);
    } else {
      alert('Session ID not found. Please try again.');
    }
  };

  // Extract years from test dates
  const availableYears = useMemo(() => {
    const years = new Set();
    tests.forEach(test => {
      if (test.date) {
        const year = test.date.split('-')[0];
        years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => b - a); // Newest first
  }, [tests]);

  // Filter tests
  const filteredTests = useMemo(() => {
    return tests.filter(test => {
      // Search filter
      const matchesSearch = test.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (test.description && test.description.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Year filter
      const matchesYear = yearFilter === 'all' || 
                         (test.date && test.date.startsWith(yearFilter));
      
      return matchesSearch && matchesYear;
    });
  }, [tests, searchQuery, yearFilter]);

  const completedTests = filteredTests.filter(t => t.completed);
  const availableTests = filteredTests.filter(t => !t.completed);
  const totalTests = tests.length;
  const completedCount = tests.filter(t => t.completed).length;
  const progressPercentage = totalTests > 0 ? (completedCount / totalTests) * 100 : 0;

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Navigation Header */}
      <nav className="dashboard-nav">
        <img src={MasterEdLogo} alt="MasterEd Logo" className="bluebook-small-logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} />
        <div className="tab-buttons">
          <button className="tab-btn" onClick={() => navigate('/dashboard')}>WEEKLY TESTS</button>
          <button className="tab-btn active">PAST PAPERS</button>
        </div>
        <div className="nav-user">
          <NotificationsBell />
          <span className="user-name">{`${studentName} [ID: ${studentId}]`}</span>
          <Tooltip title="My Profile" arrow>
            <IconButton 
              onClick={() => navigate('/profile')} 
              sx={{ 
                color: '#333',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              <AccountCircleIcon sx={{ fontSize: 32 }} />
            </IconButton>
          </Tooltip>
          <button className="logout-button" onClick={() => { authService.logout(); navigate('/login'); }}>Logout</button>
        </div>
      </nav>

      <main className="dashboard-content">
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Personalized Greeting */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              {greeting}, {firstName}! 👋
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <SchoolIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 0.5 }} />
              Take official SAT past papers. Each test can only be taken once.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Search and Filter */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search tests by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              select
              label="Filter by Year"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Years</MenuItem>
              {availableYears.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              onClick={() => { setSearchQuery(''); setYearFilter('all'); }}
              size="small"
            >
              Clear Filters
            </Button>
          </Paper>

          {/* Progress Bar */}
          <Paper elevation={2} sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" fontWeight="bold">
            Your Progress
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="primary">
            {completedCount} / {totalTests} Tests
          </Typography>
        </Box>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 12,
              borderRadius: 6,
              bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                borderRadius: 6,
                bgcolor: progressPercentage === 100 ? '#4caf50' : '#1976d2'
              }
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontWeight: 'bold',
              color: progressPercentage > 50 ? 'white' : '#666'
            }}
          >
            {progressPercentage.toFixed(0)}%
          </Typography>
        </Box>
        {progressPercentage === 100 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            🎉 Congratulations! You've completed all available tests!
          </Alert>
        )}
      </Paper>

          {/* Personal Statistics */}
          {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: '#e3f2fd' }}>
              <TrophyIcon sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="#1976d2">
                {stats.completedTests || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tests Completed
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: '#e8f5e9' }}>
              <TrendingUpIcon sx={{ fontSize: 48, color: '#2e7d32', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="#2e7d32">
                {stats.bestScore ? `${stats.bestScore.toFixed(1)}%` : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Best Score
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: '#fff3e0' }}>
              <AssessmentIcon sx={{ fontSize: 48, color: '#f57c00', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="#f57c00">
                {stats.averageScore ? `${stats.averageScore.toFixed(1)}%` : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Score
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

          {/* Available Tests */}
          {availableTests.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
            Available Tests
          </Typography>
          <Grid container spacing={3}>
            {availableTests.map((test) => (
              <Grid item xs={12} md={6} key={test.id}>
                <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
                        {test.name}
                      </Typography>
                      <Chip 
                        label="Available" 
                        color="success" 
                        size="medium"
                        sx={{ 
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          boxShadow: '0 2px 4px rgba(46, 125, 50, 0.2)'
                        }}
                      />
                    </Box>
                    {test.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {test.description}
                      </Typography>
                    )}
                    {test.date && (
                      <Typography variant="caption" color="text.secondary">
                        Test Date: {test.date}
                      </Typography>
                    )}
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleStartTest(test.id)}
                      sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
                    >
                      Start Test
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

          {/* Completed Tests */}
          {completedTests.length > 0 && (
        <Box>
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
            Completed Tests
          </Typography>
          <Grid container spacing={3}>
            {completedTests.map((test) => (
              <Grid item xs={12} md={6} key={test.id}>
                <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
                        {test.name}
                      </Typography>
                      <Chip 
                        label="Completed" 
                        color="primary" 
                        size="medium"
                        icon={<CheckCircleIcon />}
                        sx={{ 
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)'
                        }}
                      />
                    </Box>
                    {test.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {test.description}
                      </Typography>
                    )}
                    {test.date && (
                      <Typography variant="caption" color="text.secondary">
                        Test Date: {test.date}
                      </Typography>
                    )}
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleViewResults(test)}
                      sx={{
                        bgcolor: '#1976d2',
                        '&:hover': { bgcolor: '#115293' },
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.5
                      }}
                    >
                      VIEW RESULTS
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

          {/* No tests message */}
          {filteredTests.length === 0 && !loading && tests.length > 0 && (
            <Alert severity="info">
              No tests match your filters. Try adjusting the search or year filter.
            </Alert>
          )}

          {tests.length === 0 && !loading && (
        <Alert severity="info">
          No tests available at the moment. Check back soon!
        </Alert>
      )}

          {/* Recommendation */}
          {availableTests.length > 0 && (
        <Box sx={{ mt: 4, p: 3, bgcolor: '#fff8e1', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold" color="#f57c00">
            💡 Recommendation
          </Typography>
          <Typography variant="body2">
            {completedTests.length === 0
              ? `Start with "${availableTests[0].name}" to get your baseline score!`
              : stats.bestScore < 70
              ? 'Continue practicing to improve your score. Focus on reviewing your mistakes!'
              : 'Great progress! Keep taking tests to maintain your performance.'}
          </Typography>
        </Box>
      )}
        </Container>
      </main>
    </div>
  );
};

export default Leaks;
